import type { DuplicateFlag, DuplicateSide } from "./crm-types";

/**
 * Duplicate flags, gathered into the thing a person actually decides about.
 *
 * The detector records a *pair*, which is the right unit for it -- a suspicion
 * is about two records -- and the wrong unit for everything downstream. Four
 * unfiled depots on one domain are six flags, and the queue that drew one card
 * per flag asked the same question six times, with each of the four names on
 * screen three or four times over.
 *
 * A cluster is every flag sharing one piece of evidence, and the companies
 * behind them. Three consumers need it and none of them should re-derive it:
 * the queue, the banner on a company page, and the sidebar count -- which used
 * to say eight while the page showed three things to look at.
 */

export interface Cluster {
  /** Reason and matched value, which is what makes it one cluster. */
  key: string;
  matchedValue: string | null;
  flags: DuplicateFlag[];
  companies: DuplicateSide[];
}

/**
 * Flags split into the domains they are about.
 *
 * A pair is the unit of a merge and the wrong unit to decide in: four unfiled
 * depots on one domain are six flags, and answering them a pair at a time does
 * not work even in principle -- the second pair asks a company that now has
 * branches to become one.
 *
 * Built from the flags rather than asked of the server. The queue holds every
 * pair on the domain and a company page holds every pair touching that
 * company, so the set is already there: one request fewer, and no second
 * definition of "the same match" to drift from the detector's.
 *
 * Status is the caller's business. The queue asks for one status at a time and
 * a company page shows only open flags, so filtering again here would only be
 * a way for the dismissed and merged tabs to come back empty -- which is what
 * it used to do.
 */
export function clustersOf(flags: DuplicateFlag[]): Cluster[] {
  const byEvidence = new Map<string, DuplicateFlag[]>();

  for (const flag of flags) {
    if (flag.other === null) continue;
    const key = `${flag.reason}|${flag.matchedValue ?? ""}`;
    byEvidence.set(key, [...(byEvidence.get(key) ?? []), flag]);
  }

  return [...byEvidence.entries()].map(([key, group]) => ({
    key,
    matchedValue: group[0]?.matchedValue ?? null,
    flags: group,
    companies: clusterOf(group),
  }));
}

/** The companies behind a set of flags, one entry each, in id order. */
export function clusterOf(flags: DuplicateFlag[]): DuplicateSide[] {
  const found = new Map<number, DuplicateSide>();

  for (const flag of flags) {
    for (const side of [flag.company, flag.other]) {
      if (side) found.set(side.id, side);
    }
  }

  return [...found.values()].sort((a, b) => a.id - b.id);
}
